import React, { useEffect, useRef, useState } from 'react'
import { WHEEL500_COLORS, WHEEL500_PHASES, WHEEL500_TIMINGS } from '../mock/wheel500Mock'

/**
 * Горизонтальная лента секторов с центральным курсором.
 * При входе в фазу spinning крутит ленту и плавно останавливается на winSectorIndex.
 */

const SECTOR_WIDTH = 72
const SECTOR_GAP = 4
const FULL_LOOPS = 8 // сколько полных оборотов делаем перед остановкой

const STRIP_THEME = {
  esports: {
    frameClass: 'rounded-2xl border border-cyan-400/25 bg-slate-900/60 p-3 shadow-[0_10px_30px_rgba(6,182,212,0.18)]',
    leftFadeClass: 'pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-slate-950 to-transparent',
    rightFadeClass: 'pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-slate-950 to-transparent',
    cursorClass: 'bg-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.8)]',
    cursorTipClass: 'bg-cyan-300 shadow-[0_0_8px_rgba(34,211,238,0.8)]'
  },
  casino: {
    frameClass: 'rounded-2xl border border-amber-300/30 bg-gradient-to-r from-amber-950/45 via-rose-950/35 to-amber-950/45 p-3 shadow-[0_12px_34px_rgba(245,158,11,0.18)]',
    leftFadeClass: 'pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-amber-950/80 to-transparent',
    rightFadeClass: 'pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-amber-950/80 to-transparent',
    cursorClass: 'bg-amber-300 shadow-[0_0_12px_rgba(252,211,77,0.8)]',
    cursorTipClass: 'bg-amber-300 shadow-[0_0_8px_rgba(252,211,77,0.8)]'
  },
  minimal: {
    frameClass: 'rounded-2xl border border-white/10 bg-dark-primary/50 p-3',
    leftFadeClass: 'pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-dark-primary to-transparent',
    rightFadeClass: 'pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-dark-primary to-transparent',
    cursorClass: 'bg-accent-primary shadow-[0_0_12px_rgba(0,255,127,0.7)]',
    cursorTipClass: 'bg-accent-primary shadow-[0_0_8px_rgba(0,255,127,0.7)]'
  }
}

const Wheel500Strip = ({ sectors, status, winSectorIndex, roundId, designVariant = 'minimal' }) => {
  const [offset, setOffset] = useState(0)
  const [transition, setTransition] = useState('none')
  const containerRef = useRef(null)
  const [containerWidth, setContainerWidth] = useState(0)
  const activeTheme = STRIP_THEME[designVariant] || STRIP_THEME.minimal

  // Отслеживаем ширину контейнера, чтобы центрировать выигрышный сектор
  useEffect(() => {
    if (!containerRef.current) return
    const measure = () => {
      setContainerWidth(containerRef.current?.offsetWidth || 0)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  // Управление анимацией по статусу раунда
  useEffect(() => {
    const itemWidth = SECTOR_WIDTH + SECTOR_GAP
    const total = sectors.length

    if (status === WHEEL500_PHASES.BETTING) {
      // Сброс к началу без анимации в момент betting
      setTransition('none')
      setOffset(0)
      return
    }

    if (status === WHEEL500_PHASES.SPINNING && Number.isInteger(winSectorIndex) && containerWidth > 0) {
      // Сначала мгновенно сбрасываем, потом следующим кадром запускаем плавный перевод.
      setTransition('none')
      setOffset(0)

      // Центрируем выигрышный сектор: смещение = N*loops*total*itemWidth + winIdx*itemWidth - (containerWidth - itemWidth)/2
      const centerCorrection = (containerWidth - SECTOR_WIDTH) / 2
      const target = FULL_LOOPS * total * itemWidth + winSectorIndex * itemWidth - centerCorrection

      requestAnimationFrame(() => {
        setTransition(`transform ${WHEEL500_TIMINGS.SPINNING_MS}ms cubic-bezier(0.15, 0.85, 0.25, 1)`)
        setOffset(target)
      })
    }
    // На фазе result оставляем как есть
  }, [status, winSectorIndex, roundId, containerWidth, sectors.length])

  // Дублируем ленту, чтобы анимация прокрутки выглядела бесконечной
  const repeated = []
  for (let r = 0; r < FULL_LOOPS + 1; r += 1) {
    repeated.push(...sectors)
  }

  const isResult = status === WHEEL500_PHASES.RESULT

  return (
    <div className={activeTheme.frameClass}>
      <div
        ref={containerRef}
        className="relative overflow-hidden rounded-xl"
        style={{ height: SECTOR_WIDTH + 12 }}
      >
        {/* Центральный курсор */}
        <div className="pointer-events-none absolute left-1/2 top-0 z-20 h-full -translate-x-1/2">
          <div className={`h-full w-[2px] ${activeTheme.cursorClass}`} />
          <div className={`absolute -top-1 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 ${activeTheme.cursorTipClass}`} />
        </div>

        {/* Затемнение по краям */}
        <div className={activeTheme.leftFadeClass} />
        <div className={activeTheme.rightFadeClass} />

        {/* Сама лента */}
        <div
          className="flex h-full items-center"
          style={{
            gap: `${SECTOR_GAP}px`,
            transform: `translateX(-${offset}px)`,
            transition,
            willChange: 'transform'
          }}
        >
          {repeated.map((sector, idx) => {
            const colorCfg = WHEEL500_COLORS[sector.color]
            const isWinner =
              isResult &&
              Number.isInteger(winSectorIndex) &&
              sector.index === winSectorIndex &&
              idx >= FULL_LOOPS * sectors.length // подсветим только конечную копию

            return (
              <div
                key={`${idx}-${sector.index}`}
                className={`relative flex shrink-0 flex-col items-center justify-center rounded-lg text-white shadow-inner ${colorCfg.bgClass} ${isWinner ? `ring-4 ${colorCfg.ringClass} scale-105` : ''}`}
                style={{ width: SECTOR_WIDTH, height: SECTOR_WIDTH }}
              >
                <span className="text-xs opacity-80">#{sector.index}</span>
                <span className="text-base font-bold">×{sector.multiplier}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default Wheel500Strip
